/**
 * Shared utility for building test history navigation URLs.
 *
 * Both RunHistoryChart and TestFailureDrawer need to construct URLs that point
 * to either a workflow-level or job-level test page. This module centralises
 * that logic so the two components stay in sync.
 */

export interface BuildTestUrlParams {
  org: string;
  runId: string;
  testId?: string | null;
  /** The job ID of the *current* page context (empty/undefined = workflow level). */
  jobId?: string;
  /** The job ID of the *target* run (used in job-level URLs). */
  targetJobId?: string | null;
  timeRangeHours?: number;
  branchFilter?: string;
}

/**
 * Build a URL that navigates to a specific test within a workflow or job run.
 *
 * - Workflow level (`jobId` is empty): `/{org}/runs/{runId}?tab=tests&test_id={testId}`
 * - Job level: `/{org}/runs/{runId}/jobs/{targetJobId}?tab=tests#test={testId}`
 */
export function buildTestUrl({
  org,
  runId,
  testId,
  jobId,
  targetJobId,
  timeRangeHours,
  branchFilter,
}: BuildTestUrlParams): string {
  const isWorkflowLevel = !jobId || jobId === "";

  const timeRangeParam = timeRangeHours ? `&timeRange=${timeRangeHours}` : "";
  const branchFilterParam =
    branchFilter && branchFilter !== "all"
      ? `&branchFilter=${branchFilter}`
      : "";

  if (isWorkflowLevel) {
    const testIdParam = testId ? `&test_id=${testId}` : "";
    return `/${org}/runs/${runId}?tab=tests${testIdParam}${timeRangeParam}${branchFilterParam}`;
  } else {
    const resolvedJobId = targetJobId || jobId;
    const testFragment = testId ? `#test=${testId}` : "";
    return `/${org}/runs/${runId}/jobs/${resolvedJobId}?tab=tests${timeRangeParam}${branchFilterParam}${testFragment}`;
  }
}
